import { useMemo } from 'react';
import { FlexColumn } from '@elementary/common-ui/components';
import { UseFormReturn } from 'react-hook-form';
import { AddTestForm, ColumnResource, Resource } from 'shared/types';
import ColumnsAcceptedValues from './WizardConfigurationStagePages/ColumnsAcceptedValues';
import ColumnsNotNull from './WizardConfigurationStagePages/ColumnsNotNull';
import ColumnsRelationship from './WizardConfigurationStagePages/ColumnsRelationship';
import ColumnsUnique from './WizardConfigurationStagePages/ColumnsUnique';
import CustomQuery from './WizardConfigurationStagePages/CustomQuery';
import TablesDimensionAnomalies from './WizardConfigurationStagePages/TablesDimensionAnomalies';
import TablesEventFreshnessAnomalies from './WizardConfigurationStagePages/TablesEventFreshnessAnomalies';
import TablesFreshnessAnomalies from './WizardConfigurationStagePages/TablesFreshnessAnomalies';
import TablesFreshnessAnomaliesCloud from './WizardConfigurationStagePages/TablesFreshnessAnomaliesCloud';
import TablesPipelineTaskPerformanceCloud from './WizardConfigurationStagePages/TablesPipelineTaskPerformanceCloud';
import TablesDataContract from './WizardConfigurationStagePages/TablesDataContract';
import TablesSchemaChangesAnomalies from './WizardConfigurationStagePages/TablesSchemaChangesAnomalies';
import TablesVolumeAnomalies from './WizardConfigurationStagePages/TablesVolumeAnomalies';
import TablesVolumeAnomaliesCloud from './WizardConfigurationStagePages/TablesVolumeAnomaliesCloud';
import YamlTest from './WizardConfigurationStagePages/YamlTest';

type Props = {
  methods: UseFormReturn<AddTestForm, any, undefined>;
  columnsResourcesData?: ColumnResource[];
  singularTestPaths?: string[];
  tableResourcesData?: Resource[];
  editMode?: boolean;
};

const WizardConfigurationStage = ({
  methods,
  columnsResourcesData = [],
  singularTestPaths = [],
  tableResourcesData = [],
  editMode,
}: Props) => {
  const resourceType = methods.watch('resourceType');
  const testType = methods.watch('testType');
  const resources = methods.watch('resources');

  const timestampColumns = useMemo(() => {
    const columnsCount = columnsResourcesData.reduce((acc, col) => {
      if (col.type === 'timestamp' && resources?.includes(col.model_id)) {
        acc[col.name] = (acc[col.name] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(columnsCount)
      .filter(([, count]) => count === resources?.length)
      .map(([name]) => ({
        label: name,
        value: name,
      }));
  }, [columnsResourcesData, resources]);

  if (testType === 'customQuery') {
    return (
      <FlexColumn sx={{ p: 3 }}>
        <CustomQuery
          methods={methods}
          singularTestPaths={singularTestPaths}
          tableResourcesData={tableResourcesData}
        />
      </FlexColumn>
    );
  }

  if (testType === 'yamlTest') {
    return (
      <FlexColumn sx={{ p: 3, height: '100%' }}>
        <YamlTest
          methods={methods}
          appliesTo={resourceType === 'tables' ? 'table' : 'column'}
          editMode={editMode}
        />
      </FlexColumn>
    );
  }

  if (resourceType === 'tables') {
    if (testType === 'freshnessAnomalies') {
      return (
        <FlexColumn sx={{ p: 3 }}>
          <TablesFreshnessAnomalies
            methods={methods}
            timestampColumns={timestampColumns}
          />
        </FlexColumn>
      );
    }
    if (testType === 'cloudFreshnessAnomalies') {
      return (
        <FlexColumn sx={{ p: 3 }}>
          <TablesFreshnessAnomaliesCloud methods={methods} />
        </FlexColumn>
      );
    }
    if (testType === 'eventFreshnessAnomalies') {
      return (
        <FlexColumn sx={{ p: 3 }}>
          <TablesEventFreshnessAnomalies
            methods={methods}
            timestampColumns={timestampColumns}
          />
        </FlexColumn>
      );
    }
    if (testType === 'volumeAnomalies') {
      return (
        <FlexColumn sx={{ p: 3 }}>
          <TablesVolumeAnomalies
            methods={methods}
            timestampColumns={timestampColumns}
          />
        </FlexColumn>
      );
    }
    if (testType === 'cloudVolumeAnomalies') {
      return (
        <FlexColumn sx={{ p: 3 }}>
          <TablesVolumeAnomaliesCloud methods={methods} />
        </FlexColumn>
      );
    }
    if (testType === 'dimensionAnomalies') {
      return (
        <FlexColumn sx={{ p: 3 }}>
          <TablesDimensionAnomalies
            methods={methods}
            timestampColumns={timestampColumns}
          />
        </FlexColumn>
      );
    }
    if (testType === 'schemaChanges') {
      return (
        <FlexColumn sx={{ p: 3 }}>
          <TablesSchemaChangesAnomalies methods={methods} />
        </FlexColumn>
      );
    }
    if (testType === 'cloudDataContract') {
      return (
        <FlexColumn sx={{ p: 3 }}>
          <TablesDataContract methods={methods} />
        </FlexColumn>
      );
    }
    if (testType === 'cloudPipelineTaskPerformance') {
      return (
        <FlexColumn sx={{ p: 3 }}>
          <TablesPipelineTaskPerformanceCloud methods={methods} />
        </FlexColumn>
      );
    }
  }

  if (resourceType === 'columns') {
    if (testType === 'unique') {
      return (
        <FlexColumn sx={{ p: 3 }}>
          <ColumnsUnique
            methods={methods}
            columnsResourcesData={columnsResourcesData}
          />
        </FlexColumn>
      );
    }
    if (testType === 'notNull') {
      return (
        <FlexColumn sx={{ p: 3 }}>
          <ColumnsNotNull
            methods={methods}
            columnsResourcesData={columnsResourcesData}
          />
        </FlexColumn>
      );
    }
    if (testType === 'acceptedValues') {
      return (
        <FlexColumn sx={{ p: 3 }}>
          <ColumnsAcceptedValues
            methods={methods}
            columnsResourcesData={columnsResourcesData}
          />
        </FlexColumn>
      );
    }
    if (testType === 'relationship') {
      return (
        <FlexColumn sx={{ p: 3 }}>
          <ColumnsRelationship
            methods={methods}
            columnsResourcesData={columnsResourcesData}
          />
        </FlexColumn>
      );
    }
  }

  return <div />;
};

export default WizardConfigurationStage;
